import { DEFAULT_MAIN_TAB_ROUTE } from './LastMainTabContext';

type NestedRoute = {
  name: string;
  params?: { screen?: string };
  state?: {
    index: number;
    routes: NestedRoute[];
  };
};

export type AppShellRouteSnapshot = {
  name: string;
  state?: {
    index: number;
    routes: NestedRoute[];
  };
};

function getNestedFocusedRouteName(route: NestedRoute | undefined): string | undefined {
  if (!route) return undefined;
  if (!route.state) {
    const screenParam = route.params?.screen;
    if (typeof screenParam === 'string' && screenParam.length > 0) return screenParam;
    return route.name;
  }
  const focused = route.state.routes[route.state.index];
  return focused?.name ?? route.params?.screen;
}

export function getShellStackFocusedRoute(appShellRoute: AppShellRouteSnapshot | undefined): string {
  if (!appShellRoute?.state) return DEFAULT_MAIN_TAB_ROUTE;
  return getNestedFocusedRouteName(appShellRoute) ?? appShellRoute.name;
}

export function resolveMainTabsActiveRoute(
  appShellRoute: AppShellRouteSnapshot | undefined
): string | undefined {
  if (!appShellRoute?.state) return undefined;

  const shellState = appShellRoute.state;
  const currentShellRoute = shellState.routes[shellState.index];
  const mainTabsRoute =
    currentShellRoute?.name === 'MainTabs'
      ? currentShellRoute
      : shellState.routes.find((route) => route.name === 'MainTabs');

  if (!mainTabsRoute) return undefined;

  const fromState = getNestedFocusedRouteName(mainTabsRoute);
  if (fromState && fromState !== 'MainTabs') return fromState;

  const screenParam = mainTabsRoute.params?.screen;
  if (typeof screenParam === 'string' && screenParam.length > 0) return screenParam;

  return undefined;
}

/**
 * Resuelve la ruta activa del sidebar web desde el estado anidado AppShell → ShellStack → MainTabs.
 */
export function resolveSidebarActiveRoute(
  appShellRoute: AppShellRouteSnapshot | undefined,
  fallbackTabName?: string
): string {
  const shellFocused = getShellStackFocusedRoute(appShellRoute);
  if (shellFocused !== 'MainTabs') return shellFocused;

  return (
    resolveMainTabsActiveRoute(appShellRoute) ??
    fallbackTabName ??
    DEFAULT_MAIN_TAB_ROUTE
  );
}
